import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useConfirmSourcingEventClassificationMutation } from "../../../../../autogen/bff-api";
import { coordinatorRegistry } from "../../../../../common/editing/save-coordinator";
import { useApiError } from "../../../../../common/errors/useApiError";
import { useProcurementTerms } from "../../../../../common/terminology/useProcurementTerms";

interface Props {
  eventId: string;
  /** The regime the procurement holds today — the change moves it to the other one. */
  currentRegime: "Public" | "Private";
}

/**
 * Moving between the regimes replaces the event context, which drops what the other regime cannot
 * hold. That is the buyer's call to make, but never a side effect of editing a value, so it lives
 * behind its own button and names what it costs before doing it.
 */
export const ChangeRegimeButton = ({ eventId, currentRegime }: Props) => {
  const { t } = useTranslation();
  const terms = useProcurementTerms();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [confirmClassification, { isLoading }] = useConfirmSourcingEventClassificationMutation();
  const { trigger } = useApiError();

  const becomesPrivate = currentRegime === "Public";

  const change = async () => {
    await coordinatorRegistry.flush(eventId);
    try {
      const changed = await confirmClassification({ eventId }).unwrap();
      coordinatorRegistry.get(eventId)?.setServerState(changed);
      onClose();
    } catch (e) {
      trigger(e);
    }
  };

  return (
    <>
      <Button mt="2" size="sm" variant="outline" onClick={onOpen}>
        {becomesPrivate ? terms.changeToSimplified : t("Change to a public procurement")}
      </Button>

      <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {becomesPrivate ? terms.changeToSimplifiedQuestion : t("Change to a public procurement?")}
            </AlertDialogHeader>
            <AlertDialogBody>
              <Text fontSize="sm">
                {becomesPrivate
                  ? t("The governing regulation, the applicable part, CPV codes, the procedure and the notice data are discarded. The title, value, contract type, requirements and documents are kept.")
                  : t("The suppliers you have invited are discarded. The title, value, contract type, requirements and documents are kept.")}
              </Text>
              <Text fontSize="sm" mt="2">
                {t("This cannot be undone.")}
              </Text>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose} variant="ghost">
                {t("Cancel")}
              </Button>
              <Button colorScheme="red" ml="3" isLoading={isLoading} onClick={change}>
                {becomesPrivate ? terms.changeToSimplifiedShort : t("Change to public")}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};
